/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// State
import { TStore } from '@store/index';
import { StateCreator } from 'zustand';

import { TOrganization } from '@global-types/api/api';
import {
	TQueryParams,
	TSortStatus,
} from '@global-types/index';

/* -------------------------------------------------------------------------- */
/*                                  Interface                                 */
/* -------------------------------------------------------------------------- */
export interface IOrganizationSlice {
	organizations: TOrganization[];
	organizationsTotal: number;
	organizationsPage: number;
	organizationsPageSize: number;
	organizationsTotalPages: number;
	organizationsSelectedRecords: TOrganization[];
	organizationsQueryParams: TQueryParams;
	organizationsSelectedRow: Partial<TOrganization>;
	organizationsSortParams: string;
	organizationOrganization: string | null;
	organizationsSortStatus: {};
	organizationsImportErrors: string | null;
	organizationsLastAccessTokenCreated: string | null;
	organizationsOpenConfirmationModalDelete: {
		open: boolean;
		params: string;
	};
	organizationsOpenConfirmationModalVerify: {
		open: boolean;
		params: string;
	};
	organizationsOpenConfirmationModalMint: {
		open: boolean;
		params: string;
	};

	setOrganizationsLastAccessTokenCreated: (
		_organizationsLastAccessTokenCreated: string
	) => void;

	setOrganizationsImportErrors: (_errors: string) => void;

	setOrganizationOrganization: (
		_organizationId: string | null
	) => void;

	setOrganizationsPage: (
		_organizationsPage: number
	) => void;

	setOrganizationsSelectedRecords: (
		_organizationsSelectedRecords: TOrganization[]
	) => void;

	setOrganizationsOpenConfirmationModalDelete: (_organizationsOpenConfirmationModalDelete: {
		open: boolean;
		params: any;
	}) => void;

	setOrganizationsOpenConfirmationModalVerify: (_organizationsOpenConfirmationModalVerify: {
		open: boolean;
		params: any;
	}) => void;
	setOrganizationsOpenConfirmationModalMint: (_organizationsOpenConfirmationModalMint: {
		open: boolean;
		params: any;
	}) => void;

	setOrganizationsQueryParams: (
		_organizationsQueryParams: TQueryParams
	) => void;

	setOrganizationsSortStatus: (
		_organizationsSortStatus: TSortStatus
	) => void;
	setOrganizationsSortParams: (
		_organizationsSortParams: string
	) => void;

	setOrganizationsSelectedRow: (
		_organization: TOrganization
	) => void;
	setOrganizationsPageSize: (_pageSize: number) => void;
}

/* -------------------------------------------------------------------------- */
/*                                    Slice                                   */
/* -------------------------------------------------------------------------- */

const initialState = {
	organizations: [],
	organizationsTotal: 0,
	organizationsPage: 1,
	organizationsPageSize: 100,
	organizationsTotalPages: 0,
	organizationsSelectedRecords: [],
	organizationsQueryParams: {},
	organizationsSelectedRow: {},
	organizationsSortParams: '-createdAt',
	organizationOrganization: null,
	organizationsSortStatus: {},
	organizationsImportErrors: null,
	organizationsLastAccessTokenCreated: null,
	organizationsOpenConfirmationModalDelete: {
		open: false,
		params: '',
	},
	organizationsOpenConfirmationModalVerify: {
		open: false,
		params: '',
	},
	organizationsOpenConfirmationModalMint: {
		open: false,
		params: '',
	},
};

export const createOrganizationsSlice: StateCreator<
	TStore,
	[],
	[],
	IOrganizationSlice
> = (set) => ({
	...initialState,
	setOrganizationOrganization: (
		_organizationId: string | null
	) => set({ organizationOrganization: _organizationId }),

	setOrganizationsLastAccessTokenCreated: (
		_organizationsLastAccessTokenCreated: string
	) =>
		set({
			organizationsLastAccessTokenCreated:
				_organizationsLastAccessTokenCreated,
		}),

	setOrganizationsImportErrors: (_errors: string) =>
		set({ organizationsImportErrors: _errors }),

	setOrganizationsSelectedRow: (
		_organization: TOrganization
	) => set({ organizationsSelectedRow: _organization }),

	setOrganizationsSortParams: (_sortStatus: string) =>
		set({ organizationsSortParams: _sortStatus }),

	setOrganizationsSortStatus: (
		_sortStatus: TSortStatus
	) => set({ organizationsSortStatus: _sortStatus }),

	setOrganizationsQueryParams: (
		_queryParams: TQueryParams
	) => set({ organizationsQueryParams: _queryParams }),
	setOrganizationsTotal: (_total: number) =>
		set({ organizationsTotal: _total }),
	setOrganizationsOpenConfirmationModalDelete:
		(_organizationsOpenConfirmationModalDelete: {
			open: boolean;
			params: any;
		}) =>
			set({
				organizationsOpenConfirmationModalDelete:
					_organizationsOpenConfirmationModalDelete,
			}),
	setOrganizationsOpenConfirmationModalMint:
		(_organizationsOpenConfirmationModalMint: {
			open: boolean;
			params: string;
		}) =>
			set({
				organizationsOpenConfirmationModalMint:
					_organizationsOpenConfirmationModalMint,
			}),
	setOrganizationsOpenConfirmationModalVerify:
		(_organizationsOpenConfirmationModalVerify: {
			open: boolean;
			params: string;
		}) =>
			set({
				organizationsOpenConfirmationModalVerify:
					_organizationsOpenConfirmationModalVerify,
			}),
	setOrganizationsPage: (_page: number) =>
		set({ organizationsPage: _page }),
	setOrganizationsPageSize: (_pageSize: number) =>
		set({ organizationsPageSize: _pageSize }),
	setOrganizationsTotalPages: (_totalPages: number) =>
		set({ organizationsTotalPages: _totalPages }),
	setOrganizationsSelectedRecords: (
		_organizationsSelectedRecords: TOrganization[]
	) =>
		set({
			organizationsSelectedRecords:
				_organizationsSelectedRecords,
		}),
});
